import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ChevronUp, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SortableList } from "@/components/SortableList";
import { RichEditor } from "@/components/rich/RichEditor";
import { api, errMsg } from "@/lib/api";
import { STAGES, imgUrl } from "@/lib/siteNews";
import { ImageField } from "@/components/admin/siteNews/ImageField";

const ItemDialog = ({ item, onClose }) => {
  const qc = useQueryClient();
  const [f, setF] = useState({ title: "", description: "", stage: "planned", eta: "", image_id: null, published: true, ...item });
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    const body = { title: f.title.trim(), description: f.description, stage: f.stage, eta: f.eta.trim(), image_id: f.image_id, published: f.published };
    try {
      if (item?.id) await api.patch(`/admin/site/roadmap/${item.id}`, body); else await api.post("/admin/site/roadmap", body);
      qc.invalidateQueries({ queryKey: ["admin-roadmap"] }); qc.invalidateQueries({ queryKey: ["site-updates"] });
      toast.success("Feature saved");
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto" data-testid="roadmap-item-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{item?.id ? "Edit coming feature" : "New coming feature"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Input value={f.title} onChange={(e) => set("title")(e.target.value)} maxLength={120} placeholder="Feature name" data-testid="roadmap-item-title" />
          <div className="rounded border border-[#2D3442]"><RichEditor value={f.description} onChange={set("description")} placeholder="What is it and why will writers love it?" minH="min-h-[120px]" testid="roadmap-item-desc" /></div>
          <div className="flex flex-wrap gap-3">
            <Select value={f.stage} onValueChange={set("stage")}>
              <SelectTrigger className="w-44" data-testid="roadmap-item-stage"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(STAGES).map(([k, [l]]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <Input value={f.eta} onChange={(e) => set("eta")(e.target.value)} maxLength={40} placeholder='Expected, e.g. "Spring 2026"' className="w-56" data-testid="roadmap-item-eta" />
            <label className="flex items-center gap-2 text-sm text-[#E8DFD1]"><Switch checked={f.published} onCheckedChange={set("published")} data-testid="roadmap-item-published" />Show on the page</label>
          </div>
          <ImageField value={f.image_id} onChange={set("image_id")} testid="roadmap-item-image" />
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={!f.title.trim()} data-testid="roadmap-item-save">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Row = ({ item, onEdit }) => {
  const qc = useQueryClient();
  const del = async () => {
    if (!window.confirm(`Delete “${item.title}”? Its votes are deleted too.`)) return;
    try { await api.delete(`/admin/site/roadmap/${item.id}`); qc.invalidateQueries({ queryKey: ["admin-roadmap"] }); qc.invalidateQueries({ queryKey: ["site-updates"] }); } catch (e) { toast.error(errMsg(e)); }
  };
  const [label, color] = STAGES[item.stage] || STAGES.planned;
  return (
    <div className="flex items-center gap-3 rounded border border-[#2D3442] bg-[#0F131A] p-2.5" data-testid={`roadmap-row-${item.id}`}>
      {item.image_id ? <img src={imgUrl(item.image_id)} alt="" className="h-10 w-16 rounded object-cover" /> : <div className="h-10 w-16 rounded bg-[#1A202C]" />}
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm text-[#F3EBDD]">{item.title}{!item.published && <EyeOff className="ml-1.5 inline h-3.5 w-3.5 text-[#A8A090]" />}</div>
        <div className="text-[11px]" style={{ color }}>{label}{item.eta && <span className="text-[#A8A090]"> · {item.eta}</span>}</div>
      </div>
      <span className="flex items-center gap-0.5 text-sm text-[#FBBF24]" title="Votes" data-testid={`roadmap-votes-${item.id}`}><ChevronUp className="h-4 w-4" />{item.votes || 0}</span>
      <button onClick={onEdit} className="rounded p-1.5 text-[#A8A090] hover:bg-[#1A202C] hover:text-[#F3EBDD]" data-testid={`roadmap-edit-${item.id}`}><Pencil className="h-4 w-4" /></button>
      <button onClick={del} className="rounded p-1.5 text-[#A8A090] hover:bg-[#1A202C] hover:text-[#F87171]" data-testid={`roadmap-delete-${item.id}`}><Trash2 className="h-4 w-4" /></button>
    </div>
  );
};

export const IntroEditor = () => {
  const { data } = useQuery({ queryKey: ["admin-intro"], queryFn: () => api.get("/admin/site/intro").then((r) => r.data) });
  const [text, setText] = useState(null);
  const save = async () => { try { await api.put("/admin/site/intro", { intro: text }); toast.success("Intro saved"); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <section className="rounded-md border border-[#2D3442] bg-[#13171F] p-6" data-testid="site-intro-editor">
      <h3 className="font-display text-2xl text-[#F3EBDD]">Page intro</h3>
      <p className="mb-3 text-sm text-[#A8A090]">Shown at the top of the Building LoreRight page (/updates).</p>
      <Textarea value={text ?? data?.intro ?? ""} onChange={(e) => setText(e.target.value)} rows={4} maxLength={5000} data-testid="site-intro-text" />
      <Button className="mt-3" size="sm" onClick={save} disabled={text === null} data-testid="site-intro-save">Save intro</Button>
    </section>
  );
};

export const RoadmapAdmin = () => {
  const qc = useQueryClient();
  const { data: items = [] } = useQuery({ queryKey: ["admin-roadmap"], queryFn: () => api.get("/admin/site/roadmap").then((r) => r.data) });
  const [edit, setEdit] = useState(null);
  const reorder = async (next) => {
    qc.setQueryData(["admin-roadmap"], next);
    try { await api.put("/admin/site/roadmap-order", { ids: next.map((i) => i.id) }); qc.invalidateQueries({ queryKey: ["site-updates"] }); } catch (e) { toast.error(errMsg(e)); qc.invalidateQueries({ queryKey: ["admin-roadmap"] }); qc.invalidateQueries({ queryKey: ["site-updates"] }); }
  };
  return (
    <section className="rounded-md border border-[#2D3442] bg-[#13171F] p-6" data-testid="roadmap-admin">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div><h3 className="font-display text-2xl text-[#F3EBDD]">Coming soon</h3><p className="text-sm text-[#A8A090]">Drag to set the order on the page. Votes come from signed-in users.</p></div>
        <Button size="sm" onClick={() => setEdit({})} data-testid="roadmap-new"><Plus className="mr-1 h-4 w-4" />Add feature</Button>
      </div>
      {!items.length && <p className="text-sm text-[#A8A090]" data-testid="roadmap-empty">No coming features yet.</p>}
      <SortableList items={items} getKey={(i) => i.id} onReorder={reorder} scope="roadmap" testid="roadmap-list" render={(i) => <Row item={i} onEdit={() => setEdit(i)} />} />
      {edit && <ItemDialog item={edit} onClose={() => setEdit(null)} />}
    </section>
  );
};
